/**
 * Chart Redux slice.
 *
 * Central state for the multi-panel charting system built on
 * `lightweight-charts`.  Key responsibilities:
 *
 * - **Panel management** — create, remove, and configure chart panels (symbol,
 *   timeframe, chart type).  Layout is persisted in `localStorage`.
 * - **1-minute bar storage** — holds up to `MAX_BARS_PER_SYMBOL` (25,000)
 *   `OneMinuteBar` objects per symbol.  Higher timeframes are derived
 *   on-the-fly by `ChartDataHandler.convertTimeframe`.
 * - **Live tick integration** — `updateOneMinuteBar` / `batchUpdateOneMinuteBars`
 *   are dispatched every 100 ms from the WebSocket middleware to keep the
 *   current-minute bar up-to-date.
 * - **History merge** — `setBackfillData` / `prependHistoryData` merge
 *   server-provided history with any live ticks already in memory,
 *   preserving the widest OHLC from both sources.
 * - **Crosshair sync** — stores `crosshairTime` so all visible panels can
 *   render their crosshair at the same UTC timestamp.
 */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ChartPanel, ChartSymbol, TimeframeType, OneMinuteBar, DayBar, LayoutMode, ChartType } from "../types/chart.types";
import { loadChartLayout, SavedChartLayout } from "@service/local-storage-handler";
import { syncSaveChartLayout } from "@service/sync-handler";

// Max 1-minute bars per symbol kept in memory.
// 375 bars/trading-day × 250 trading-days/year × 1.5 years ≈ 140,000
// Rounded up to 150,000 to provide ~18 months of full 1-min history.
const MAX_BARS_PER_SYMBOL = 150000;

interface ChartState {
  panels: ChartPanel[];
  activeSymbols: string[]; // Symbols currently being charted
  oneMinuteData: Record<string, OneMinuteBar[]>; // symbol -> 1min bars
  eodData: Record<string, DayBar[]>;             // symbol -> EOD day bars (up to 20 years)
  eodVersion: number;                            // bumped when EOD duration changes to force re-fetch
  /** Symbols whose history (todayfill + backfill) is still loading.
   *  While a symbol is in this set, live ticks are buffered in
   *  `pendingTickBars` instead of going directly to `oneMinuteData`. */
  historyLoadingSymbols: string[];
  /** Buffered live ticks for symbols in `historyLoadingSymbols`. */
  pendingTickBars: Record<string, { symbol: string; bar: OneMinuteBar }[]>;
  layoutMode: LayoutMode;
  activeTabId: string | null; // active panel in tab mode
  marketWatchOpen: boolean;
  toolsSidebarOpen: boolean;
  crosshairSyncEnabled: boolean; // multi-chart crosshair sync
  crosshairTime: number | null;  // shared crosshair timestamp for sync
}

// Load persisted layout from localStorage
const savedLayout = loadChartLayout();

const initialState: ChartState = {
  panels: savedLayout?.panels?.map(p => ({
    id: p.id,
    symbol: p.symbol || null,
    timeframe: (p.timeframe as TimeframeType) || "1",
    chartType: (p.chartType as ChartType) || "candlestick",
    data: [],
    isLoading: false,
    error: null,
  })) || [],
  activeSymbols: savedLayout?.panels
    ?.filter(p => p.symbol?.symbol)
    .map(p => p.symbol!.symbol) || [],
  oneMinuteData: {},
  eodData: {},
  eodVersion: 0,
  historyLoadingSymbols: [],
  pendingTickBars: {},
  layoutMode: (savedLayout?.layoutMode as LayoutMode) || "tab",
  activeTabId: savedLayout?.activeTabId || null,
  marketWatchOpen: savedLayout?.marketWatchOpen || false,
  toolsSidebarOpen: savedLayout?.toolsSidebarOpen ?? false,
  crosshairSyncEnabled: true,
  crosshairTime: null,
};

/** Helper: persist current layout to localStorage */
const persistLayout = (state: ChartState) => {
  const layout: SavedChartLayout = {
    panels: state.panels.map(p => ({
      id: p.id,
      symbol: p.symbol,
      timeframe: p.timeframe,
      chartType: p.chartType,
    })),
    layoutMode: state.layoutMode,
    activeTabId: state.activeTabId,
    marketWatchOpen: state.marketWatchOpen,
    toolsSidebarOpen: state.toolsSidebarOpen,
  };
  // Write to localStorage (sync) + push to server (async, fire-and-forget)
  syncSaveChartLayout(layout);
};

const chartSlice = createSlice({
  name: "chart",
  initialState,
  reducers: {
    addPanel: (state) => {
      const newId = `panel-${Date.now()}`;
      state.panels.push({
        id: newId,
        symbol: null,
        timeframe: "1",
        chartType: "candlestick",
        data: [],
        isLoading: false,
        error: null,
      });
      // Auto-select the new panel as active tab
      state.activeTabId = newId;
      persistLayout(state);
    },
    
    removePanel: (state, action: PayloadAction<string>) => {
      if (state.panels.length > 1) {
        const panelIndex = state.panels.findIndex(p => p.id === action.payload);
        if (panelIndex !== -1) {
          const removedSymbol = state.panels[panelIndex].symbol?.symbol;
          state.panels.splice(panelIndex, 1);
          
          // Check if symbol is still used in other panels
          if (removedSymbol) {
            const stillUsed = state.panels.some(p => p.symbol?.symbol === removedSymbol);
            if (!stillUsed) {
              state.activeSymbols = state.activeSymbols.filter(s => s !== removedSymbol);
              delete state.oneMinuteData[removedSymbol];
              delete state.eodData[removedSymbol];
              state.historyLoadingSymbols = state.historyLoadingSymbols.filter(s => s !== removedSymbol);
              delete state.pendingTickBars[removedSymbol];
            }
          }

          // If removed panel was the active tab, switch to the nearest panel
          if (state.activeTabId === action.payload) {
            const newIndex = Math.min(panelIndex, state.panels.length - 1);
            state.activeTabId = state.panels[newIndex]?.id || null;
          }
          persistLayout(state);
        }
      } else {
        // Last panel — clear its symbol instead of removing it so the chart
        // area stays visible and the user can pick a new symbol.
        const panel = state.panels.find(p => p.id === action.payload);
        if (panel && panel.symbol) {
          const clearedSymbol = panel.symbol.symbol;
          panel.symbol = null;
          panel.data = [];
          panel.isLoading = false;
          panel.error = null;
          state.activeSymbols = state.activeSymbols.filter(s => s !== clearedSymbol);
          delete state.oneMinuteData[clearedSymbol];
          delete state.eodData[clearedSymbol];
          state.historyLoadingSymbols = state.historyLoadingSymbols.filter(s => s !== clearedSymbol);
          delete state.pendingTickBars[clearedSymbol];
          persistLayout(state);
        }
      }
    },
    
    setSymbol: (state, action: PayloadAction<{ panelId: string; symbol: ChartSymbol }>) => {
      const panel = state.panels.find(p => p.id === action.payload.panelId);
      if (panel) {
        const oldSymbol = panel.symbol?.symbol;
        panel.symbol = action.payload.symbol;
        panel.data = [];
        panel.isLoading = false;
        panel.error = null;
        
        // Add to active symbols so websocket middleware starts processing ticks
        const newSymbol = action.payload.symbol.symbol;
        if (!state.activeSymbols.includes(newSymbol)) {
          state.activeSymbols.push(newSymbol);
        }
        
        // Clear any stale data for the new symbol so we start fresh
        delete state.oneMinuteData[newSymbol];
        delete state.eodData[newSymbol];
        
        // Gate live ticks — buffer them until history is fully loaded
        if (!state.historyLoadingSymbols.includes(newSymbol)) {
          state.historyLoadingSymbols.push(newSymbol);
        }
        delete state.pendingTickBars[newSymbol];
        
        // Remove old symbol if not used in other panels
        if (oldSymbol && oldSymbol !== newSymbol) {
          const stillUsed = state.panels.some(p => p.symbol?.symbol === oldSymbol);
          if (!stillUsed) {
            state.activeSymbols = state.activeSymbols.filter(s => s !== oldSymbol);
            delete state.oneMinuteData[oldSymbol];
            delete state.eodData[oldSymbol];
            state.historyLoadingSymbols = state.historyLoadingSymbols.filter(s => s !== oldSymbol);
            delete state.pendingTickBars[oldSymbol];
          }
        }
        persistLayout(state);
      }
    },
    
    setTimeframe: (state, action: PayloadAction<{ panelId: string; timeframe: TimeframeType }>) => {
      const panel = state.panels.find(p => p.id === action.payload.panelId);
      if (panel) {
        panel.timeframe = action.payload.timeframe;
        persistLayout(state);
      }
    },

    setChartType: (state, action: PayloadAction<{ panelId: string; chartType: ChartType }>) => {
      const panel = state.panels.find(p => p.id === action.payload.panelId);
      if (panel) {
        panel.chartType = action.payload.chartType;
        persistLayout(state);
      }
    },
    
    updateOneMinuteBar: (state, action: PayloadAction<{ symbol: string; bar: OneMinuteBar }>) => {
      const { symbol, bar } = action.payload;
      if (!state.oneMinuteData[symbol]) {
        state.oneMinuteData[symbol] = [];
      }
      
      const bars = state.oneMinuteData[symbol];
      // Optimized: live ticks almost always update the last bar (current minute).
      // Check from the end instead of scanning the entire array.
      let existingIndex = -1;
      for (let i = bars.length - 1; i >= Math.max(0, bars.length - 3); i--) {
        if (bars[i].timestamp === bar.timestamp) {
          existingIndex = i;
          break;
        }
      }
      
      if (existingIndex !== -1) {
        const existing = bars[existingIndex];
        if (existing.tickCount === 0 && bar.tickCount > 0) {
          bars[existingIndex] = {
            ...bar,
            open: existing.open,
            high: Math.max(existing.high, bar.high),
            low: Math.min(existing.low, bar.low),
          };
        } else {
          bars[existingIndex] = bar;
        }
      } else {
        bars.push(bar);
        if (bars.length > MAX_BARS_PER_SYMBOL) {
          bars.shift();
        }
      }
    },

    /**
     * Batch update multiple symbol bars in a single dispatch.
     * Called from websocket middleware to avoid per-tick Redux dispatches.
     *
     * If a symbol's history is still loading (in `historyLoadingSymbols`),
     * the tick bars are buffered in `pendingTickBars` instead so the chart
     * doesn't render a lone tick before historical candles arrive.
     */
    batchUpdateOneMinuteBars: (state, action: PayloadAction<{ symbol: string; bar: OneMinuteBar }[]>) => {
      for (const { symbol, bar } of action.payload) {
        // ── Tick gating: buffer while history is loading ──
        if (state.historyLoadingSymbols.includes(symbol)) {
          if (!state.pendingTickBars[symbol]) {
            state.pendingTickBars[symbol] = [];
          }
          state.pendingTickBars[symbol].push({ symbol, bar });
          continue;
        }

        if (!state.oneMinuteData[symbol]) {
          state.oneMinuteData[symbol] = [];
        }
        
        const bars = state.oneMinuteData[symbol];
        let existingIndex = -1;
        for (let i = bars.length - 1; i >= Math.max(0, bars.length - 3); i--) {
          if (bars[i].timestamp === bar.timestamp) {
            existingIndex = i;
            break;
          }
        }
        
        if (existingIndex !== -1) {
          const existing = bars[existingIndex];
          if (existing.tickCount === 0 && bar.tickCount > 0) {
            bars[existingIndex] = {
              ...bar,
              open: existing.open,
              high: Math.max(existing.high, bar.high),
              low: Math.min(existing.low, bar.low),
            };
          } else {
            bars[existingIndex] = bar;
          }
        } else {
          bars.push(bar);
          if (bars.length > MAX_BARS_PER_SYMBOL) {
            bars.shift();
          }
        }
      }
    },
    
    setBackfillData: (state, action: PayloadAction<{ symbol: string; bars: OneMinuteBar[] }>) => {
      const { symbol, bars } = action.payload;
      const existing = state.oneMinuteData[symbol] || [];

      // Start with incoming history bars
      const barMap = new Map<number, OneMinuteBar>();
      bars.forEach(b => barMap.set(b.timestamp, b));

      // Merge existing (live) bars — keep wider OHLC from both sources
      existing.forEach(b => {
        const historyBar = barMap.get(b.timestamp);
        if (historyBar && b.tickCount > 0) {
          barMap.set(b.timestamp, {
            timestamp: b.timestamp,
            open: historyBar.open,
            high: Math.max(historyBar.high, b.high),
            low: Math.min(historyBar.low, b.low),
            close: b.close,
            volume: Math.max(historyBar.volume, b.volume),
            oi: historyBar.oi || b.oi || 0,
            tickCount: b.tickCount,
          });
        } else {
          barMap.set(b.timestamp, b);
        }
      });

      let merged = Array.from(barMap.values()).sort(
        (a, b) => a.timestamp - b.timestamp
      );
      // Cap to prevent unbounded memory growth — keep most recent bars
      if (merged.length > MAX_BARS_PER_SYMBOL) {
        merged = merged.slice(merged.length - MAX_BARS_PER_SYMBOL);
      }
      state.oneMinuteData[symbol] = merged;

      // Mark loading done
      state.panels.forEach(panel => {
        if (panel.symbol?.symbol === symbol && panel.isLoading) {
          panel.isLoading = false;
        }
      });
    },
    
    /**
     * Merge historical bars (todayfill / 60-day backfill) with existing
     * live data. For overlapping timestamps, MERGE to keep the widest
     * OHLC range — live bars alone only see a few websocket snapshots
     * per minute, while history bars have the real aggregated OHLC.
     */
    prependHistoryData: (state, action: PayloadAction<{ symbol: string; bars: OneMinuteBar[] }>) => {
      const { symbol, bars } = action.payload;
      if (bars.length === 0) return;
      const existing = state.oneMinuteData[symbol] || [];

      // Start with history bars
      const barMap = new Map<number, OneMinuteBar>();
      bars.forEach(b => barMap.set(b.timestamp, b));

      // Merge existing (live) bars — keep wider OHLC from both sources
      existing.forEach(b => {
        const historyBar = barMap.get(b.timestamp);
        if (historyBar && b.tickCount > 0) {
          // Both history and live data exist for this minute — merge them
          barMap.set(b.timestamp, {
            timestamp: b.timestamp,
            open: historyBar.open,                            // history has the actual opening price
            high: Math.max(historyBar.high, b.high),          // widest high from either source
            low: Math.min(historyBar.low, b.low),             // widest low from either source
            close: b.close,                                   // live has the latest close
            volume: Math.max(historyBar.volume, b.volume),    // best volume estimate
            oi: historyBar.oi || b.oi || 0,                   // best OI from either source
            tickCount: b.tickCount,                           // keep live tick count (>0 means live)
          });
        } else {
          // No overlap, or existing is also history — live/later takes priority
          barMap.set(b.timestamp, b);
        }
      });

      let merged = Array.from(barMap.values()).sort(
        (a, b) => a.timestamp - b.timestamp
      );
      // Cap to prevent unbounded memory growth — keep most recent bars (~18 months)
      if (merged.length > MAX_BARS_PER_SYMBOL) {
        merged = merged.slice(merged.length - MAX_BARS_PER_SYMBOL);
      }
      state.oneMinuteData[symbol] = merged;
    },

    /**
     * Explicitly gate a symbol's live ticks and chart rendering.
     * Called at the start of `triggerFetch()` / `triggerEodFetch()` to
     * ensure the chart rendering effect skips intermediate data states
     * while history is still being loaded.
     */
    gateHistoryLoading: (state, action: PayloadAction<{ symbol: string }>) => {
      const { symbol } = action.payload;
      if (!state.historyLoadingSymbols.includes(symbol)) {
        state.historyLoadingSymbols.push(symbol);
      }
      // Ensure the symbol is tracked for websocket tick processing.
      // Covers the case where activeSymbols was cleared by logout but
      // the panel still holds the symbol (user hasn't re-dragged it).
      if (!state.activeSymbols.includes(symbol)) {
        state.activeSymbols.push(symbol);
      }
      // Clear any stale buffered ticks
      delete state.pendingTickBars[symbol];
    },

    /**
     * Mark a symbol's history as fully loaded. Removes the symbol from
     * `historyLoadingSymbols` and flushes any buffered live ticks from
     * `pendingTickBars` into `oneMinuteData` using the same merge logic
     * as `batchUpdateOneMinuteBars`.
     *
     * Dispatched from `triggerFetch()` / `triggerEodFetch()` once both
     * todayfill AND backfill have completed (or failed).
     */
    markHistoryLoaded: (state, action: PayloadAction<{ symbol: string }>) => {
      const { symbol } = action.payload;

      // Remove from loading set
      state.historyLoadingSymbols = state.historyLoadingSymbols.filter(s => s !== symbol);

      // Flush buffered ticks into oneMinuteData
      const pending = state.pendingTickBars[symbol];
      if (pending && pending.length > 0) {
        if (!state.oneMinuteData[symbol]) {
          state.oneMinuteData[symbol] = [];
        }
        const bars = state.oneMinuteData[symbol];

        for (const { bar } of pending) {
          let existingIndex = -1;
          for (let i = bars.length - 1; i >= Math.max(0, bars.length - 3); i--) {
            if (bars[i].timestamp === bar.timestamp) {
              existingIndex = i;
              break;
            }
          }

          if (existingIndex !== -1) {
            const existing = bars[existingIndex];
            if (existing.tickCount === 0 && bar.tickCount > 0) {
              bars[existingIndex] = {
                ...bar,
                open: existing.open,
                high: Math.max(existing.high, bar.high),
                low: Math.min(existing.low, bar.low),
              };
            } else {
              bars[existingIndex] = bar;
            }
          } else {
            bars.push(bar);
            if (bars.length > MAX_BARS_PER_SYMBOL) {
              bars.shift();
            }
          }
        }
      }
      delete state.pendingTickBars[symbol];
    },

    /**
     * Store EOD (end-of-day) day bars for a symbol.
     * Called after a successful `POST /backfill { candleInterval: "1D" }` response.
     * Merges incoming bars with any already-stored bars, deduplicates by day,
     * and sorts oldest-first.
     */
    prependEodData: (state, action: PayloadAction<{ symbol: string; bars: DayBar[] }>) => {
      const { symbol, bars } = action.payload;
      if (bars.length === 0) return;
      const existing = state.eodData[symbol] || [];
      const barMap = new Map<number, DayBar>();
      // Existing takes lower priority — incoming EOD data overwrites
      existing.forEach(b => barMap.set(b.timestamp, b));
      bars.forEach(b => barMap.set(b.timestamp, b));
      state.eodData[symbol] = Array.from(barMap.values()).sort((a, b) => a.timestamp - b.timestamp);
    },

    /** Clear all cached EOD data so charts re-fetch with updated settings. */
    clearAllEodData: (state) => {
      state.eodData = {};
      state.eodVersion += 1;
    },
    
    setPanelLoading: (state, action: PayloadAction<{ panelId: string; loading: boolean }>) => {
      const panel = state.panels.find(p => p.id === action.payload.panelId);
      if (panel) {
        panel.isLoading = action.payload.loading;
      }
    },
    
    setPanelError: (state, action: PayloadAction<{ panelId: string; error: string | null }>) => {
      const panel = state.panels.find(p => p.id === action.payload.panelId);
      if (panel) {
        panel.error = action.payload.error;
        panel.isLoading = false;
      }
    },

    clearPanelError: (state, action: PayloadAction<string>) => {
      const panel = state.panels.find(p => p.id === action.payload);
      if (panel) {
        panel.error = null;
      }
    },

    setLayoutMode: (state, action: PayloadAction<LayoutMode>) => {
      state.layoutMode = action.payload;
      // When switching to tab mode, ensure there's an active tab
      if (action.payload === "tab" && state.panels.length > 0 && !state.activeTabId) {
        state.activeTabId = state.panels[0].id;
      }
      persistLayout(state);
    },

    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTabId = action.payload;
      persistLayout(state);
    },

    toggleMarketWatch: (state) => {
      state.marketWatchOpen = !state.marketWatchOpen;
      persistLayout(state);
    },

    setMarketWatchOpen: (state, action: PayloadAction<boolean>) => {
      state.marketWatchOpen = action.payload;
      persistLayout(state);
    },

    toggleToolsSidebar: (state) => {
      state.toolsSidebarOpen = !state.toolsSidebarOpen;
      persistLayout(state);
    },

    // ── Crosshair Sync ──────────────────────────────────────
    setCrosshairTime: (state, action: PayloadAction<number | null>) => {
      state.crosshairTime = action.payload;
    },

    toggleCrosshairSync: (state) => {
      state.crosshairSyncEnabled = !state.crosshairSyncEnabled;
      if (!state.crosshairSyncEnabled) {
        state.crosshairTime = null;
      }
    },

    // ── Compare Symbol Live Tick Subscriptions ────────────────
    /**
     * Add a compare symbol to activeSymbols so the WebSocket middleware
     * starts processing live ticks for it.  Ticks are NOT gated because
     * handleCompareAdd has already fetched and rendered historical data
     * before dispatching this action.
     */
    addCompareSymbol: (state, action: PayloadAction<string>) => {
      const symbol = action.payload;
      if (!state.activeSymbols.includes(symbol)) {
        state.activeSymbols.push(symbol);
      }
    },

    /**
     * Remove a compare symbol.  Only removes from activeSymbols and clears
     * cached oneMinuteData when the symbol is not also plotted in any main
     * panel — avoids disrupting the main chart if both share the same symbol.
     */
    removeCompareSymbol: (state, action: PayloadAction<string>) => {
      const symbol = action.payload;
      const stillUsedAsMainSymbol = state.panels.some(p => p.symbol?.symbol === symbol);
      if (!stillUsedAsMainSymbol) {
        state.activeSymbols = state.activeSymbols.filter(s => s !== symbol);
        delete state.oneMinuteData[symbol];
        state.historyLoadingSymbols = state.historyLoadingSymbols.filter(s => s !== symbol);
        delete state.pendingTickBars[symbol];
      }
    },
  },
});

export const {
  addPanel,
  removePanel,
  setSymbol,
  setTimeframe,
  setChartType,
  updateOneMinuteBar,
  batchUpdateOneMinuteBars,
  setBackfillData,
  prependHistoryData,
  prependEodData,
  gateHistoryLoading,
  markHistoryLoaded,
  clearAllEodData,
  setPanelLoading,
  setPanelError,
  clearPanelError,
  setLayoutMode,
  setActiveTab,
  toggleMarketWatch,
  setMarketWatchOpen,
  toggleToolsSidebar,
  setCrosshairTime,
  toggleCrosshairSync,
  addCompareSymbol,
  removeCompareSymbol,
} = chartSlice.actions;

export default chartSlice.reducer;
